/** Seconds in a 365-day year — the annualisation basis every rate-delta
 *  intrinsic fetcher uses. */
export const YEAR_SECONDS = 365 * 24 * 60 * 60

/**
 * Annualise a fractional growth measured over `elapsedSeconds` into an
 * APR **percent**:
 *
 *   apr% = growth × (yearSeconds / elapsedSeconds) × 100
 *
 * The shared tail of every rate-delta / rewards-over-window fetcher.
 */
export const annualizeGrowthPercent = (
  growth: number,
  elapsedSeconds: number,
): number => growth * (YEAR_SECONDS / elapsedSeconds) * 100

/**
 * Annualise the growth of a monotonic exchange rate into an APR percent:
 *
 *   apr% = (rateNow / rateThen − 1) × (yearSeconds / elapsedSeconds) × 100
 *
 * The single source for the pattern that stCELO, stDOT, swBTC, apxETH,
 * iBERA, stKAIA, spUSDG and stCORE each used to hand-roll inline. When
 * both rates are `bigint` the delta is divided *first*
 * (`Number(rateNow − rateThen) / Number(rateThen)`) so the small
 * per-window growth survives — on ~1e18-scale rates,
 * `Number(rateNow) / Number(rateThen) − 1` loses it to float
 * cancellation. `number` inputs (already-scaled, possibly fractional)
 * fall back to the direct ratio.
 *
 * Callers still own their sanity checks (rates ≥ floor, non-decreasing,
 * positive elapsed) — this only does the arithmetic.
 */
export const annualizeRateDeltaPercent = (
  rateNow: bigint | number,
  rateThen: bigint | number,
  elapsedSeconds: number,
): number => {
  const growth =
    typeof rateNow === 'bigint' && typeof rateThen === 'bigint'
      ? Number(rateNow - rateThen) / Number(rateThen)
      : Number(rateNow) / Number(rateThen) - 1
  return annualizeGrowthPercent(growth, elapsedSeconds)
}
